import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Clock, User, Tag, Share } from "lucide-react";
import { 
  wordpressApi, 
  WordPressComPost,
  formatDate, 
  getFeaturedImage, 
  getAuthorName,
  getPostTags,
  stripHtmlTags 
} from "../services/wordpressApi";
import { Logo } from "./Logo";

function Header() {
  return (
    <div className="relative w-full z-10 mb-12" data-name="Header">
      <div className="flex items-center w-full">
        <Link to="/" className="block">
          <Logo />
        </Link>
      </div>
    </div>
  );
}

function SocialShareButtons({ post }: { post: WordPressComPost }) {
  const [showShareMenu, setShowShareMenu] = useState(false);
  const postUrl = `${window.location.origin}/blog/${post.slug}`;
  const postTitle = post.title;
  const postExcerpt = stripHtmlTags(post.excerpt).substring(0, 200);

  const shareOnLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(postUrl)}`;
    window.open(url, '_blank', 'width=600,height=600');
  };

  const shareOnTwitter = () => {
    const text = `${postTitle} - ${postExcerpt}`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(postUrl)}`;
    window.open(url, '_blank', 'width=600,height=600');
  };

  const shareOnBluesky = () => {
    const text = `${postTitle} - ${postExcerpt} ${postUrl}`;
    const url = `https://bsky.app/intent/compose?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'width=600,height=600');
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowShareMenu(!showShareMenu)}
        className="inline-flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
      >
        <Share className="w-4 h-4" />
        <span>Share</span>
      </button>
      
      {showShareMenu && (
        <div className="absolute top-full mt-2 right-0 bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[160px] z-10">
          <button
            onClick={shareOnLinkedIn}
            className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-2"
          >
            <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
            <span>LinkedIn</span>
          </button>
          <button
            onClick={shareOnTwitter}
            className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-2"
          >
            <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
            </svg>
            <span>Twitter</span>
          </button>
          <button
            onClick={shareOnBluesky}
            className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-2"
          >
            <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-2.67-.296-5.568.628-6.383 3.364C.378 17.902 0 22.861 0 23.55c0 .688.139 1.86.902 2.203.659.299 1.664.621 4.3-1.24C7.954 22.571 10.913 18.632 12 16.518c1.087 2.114 4.046 6.053 6.798 7.995 2.636 1.861 3.641 1.539 4.3 1.24.763-.343.902-1.515.902-2.203 0-.689-.378-5.648-.624-6.479-.815-2.736-3.713-3.66-6.383-3.364-.139.016-.277.034-.415.056.138-.017.276-.036.415-.056 2.67.296 5.568-.628 6.383-3.364.246-.829.624-5.789.624-6.479 0-.688-.139-1.86-.902-2.203-.659-.299-1.664-.621-4.3 1.24C16.046 4.747 13.087 8.686 12 10.8z"/>
            </svg>
            <span>Bluesky</span>
          </button>
        </div>
      )}
    </div>
  );
}

function BlogPostDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<WordPressComPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (slug) {
      loadPost(slug);
    }
  }, [slug]);

  const loadPost = async (postSlug: string) => {
    try {
      setLoading(true);
      setError(null);
      const postData = await wordpressApi.getPostBySlug(postSlug);
      if (postData) {
        setPost(postData);
      } else {
        setError('Blog post not found.');
      }
    } catch (err) {
      setError('Failed to load blog post.');
      console.error('Error loading post:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <svg className="w-12 h-12 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{error || 'Article not found'}</h3>
          <Link 
            to="/blog" 
            className="text-blue-600 hover:text-blue-800 transition-colors"
          >
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const featuredImage = getFeaturedImage(post);
  const authorName = getAuthorName(post);
  const tags = getPostTags(post);
  const readingTime = Math.ceil(stripHtmlTags(post.content).split(' ').length / 200);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Header />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Back to Blog Button - Positioned above the white container */}
          <div className="max-w-4xl mx-auto mb-6">
            <Link
              to="/blog"
              className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-800 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Blog</span>
            </Link>
          </div>

          {/* Article Container - Centered with max width */}
          <div className="max-w-4xl mx-auto">
            <article className="bg-white rounded-xl shadow-sm overflow-hidden">
              {featuredImage && (
                <div className="aspect-[2/1] overflow-hidden">
                  <img
                    src={featuredImage}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              
              <div className="p-8 md:p-12">
                {/* Article Meta */}
                <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-gray-500 mb-6">
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center space-x-1">
                      <User className="w-4 h-4" />
                      <span>{authorName}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(post.date)}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock className="w-4 h-4" />
                      <span>{readingTime} min read</span>
                    </div>
                  </div>
                  <SocialShareButtons post={post} />
                </div>

                {/* Title */}
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  {post.title}
                </h1>

                {/* Tags */}
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-8">
                    {tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                      >
                        <Tag className="w-3 h-3" />
                        <span>{tag}</span>
                      </span>
                    ))}
                  </div>
                )}

                {/* Content - Limited to 720px width */}
                <div className="mx-auto" style={{ maxWidth: '720px' }}>
                  <div 
                    className="prose prose-lg max-w-none
                      prose-headings:text-gray-900 prose-headings:font-semibold
                      prose-p:text-gray-700 prose-p:leading-relaxed
                      prose-a:text-blue-600 prose-a:no-underline hover:prose-a:text-blue-800
                      prose-strong:text-gray-900 prose-strong:font-semibold
                      prose-ul:text-gray-700 prose-ol:text-gray-700
                      prose-li:text-gray-700 prose-li:leading-relaxed
                      prose-blockquote:border-blue-200 prose-blockquote:bg-blue-50 prose-blockquote:text-gray-700
                      prose-code:text-blue-600 prose-code:bg-gray-100 prose-code:px-1 prose-code:rounded
                      prose-img:rounded-lg prose-img:shadow-md"
                    dangerouslySetInnerHTML={{ __html: post.content }}
                  />
                </div>

                {/* Footer */}
                <div className="mt-12 pt-8 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="text-sm text-gray-500">
                      Originally published on {formatDate(post.date)}
                    </div>
                    <a
                      href={post.URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      View on WordPress.com →
                    </a>
                  </div>
                </div>
              </div>
            </article>
          </div>

          {/* Related Articles */}
          <div className="mt-12">
            <div className="text-center">
              <Link
                to="/blog"
                className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                <span>Read More Articles</span>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default BlogPostDetail; 